import { useState } from 'react'
import { Button } from '@/components/ui'
import { SEO } from '@/components/seo'

const plans = [
  {
    name: 'Professional',
    description: 'For small to medium advice firms (1-4 advisers)',
    monthlyPrice: 149,
    annualPrice: 134,
    priceLabel: '/adviser/month',
    features: [
      '10 hours transcription included',
      '50 AI reports included',
      '25 e-signatures included',
      '500 VoIP minutes included',
      'Full template library',
      'Priority support',
      'Custom templates',
      'Approval workflows',
      'Client portal access',
    ],
    cta: 'Book a Demo',
    popular: false,
  },
  {
    name: 'Team',
    description: 'For larger practices (5+ advisers)',
    monthlyPrice: 125,
    annualPrice: 113,
    priceLabel: '/adviser/month',
    features: [
      'Everything in Professional',
      'Volume discount pricing',
      '15 hours transcription per adviser',
      '75 AI reports per adviser',
      '50 e-signatures per adviser',
      '750 VoIP minutes per adviser',
      'Team management dashboard',
      'Advanced compliance reporting',
      'Dedicated success manager',
    ],
    cta: 'Book a Demo',
    popular: true,
  },
  {
    name: 'Enterprise',
    description: 'For networks and larger organisations',
    monthlyPrice: null,
    annualPrice: null,
    priceLabel: '',
    features: [
      'Everything in Team',
      'Unlimited users',
      'Custom integrations',
      'Dedicated account manager',
      'SSO / SAML',
      'Custom compliance rules',
      'API access',
      'SLA guarantee',
      'On-premise option',
    ],
    cta: 'Contact sales',
    popular: false,
  },
]

const faqs = [
  {
    question: 'How does per-adviser pricing work?',
    answer:
      'You pay per qualified adviser in your practice. Support staff and paraplanners can be added at no extra cost. Team plan pricing (£125/adviser/month) kicks in automatically once you have 5+ advisers.',
  },
  {
    question: 'Can I change plans later?',
    answer:
      'Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle. Moving from Professional to Team happens automatically when you add your 5th adviser.',
  },
  {
    question: 'Can I cancel at any time?',
    answer:
      "Yes. WealthSpace is a rolling monthly subscription with no long-term contract. Cancel anytime from your account settings - there are no exit fees.",
  },
  {
    question: 'Is there a setup fee?',
    answer:
      'No, there are no setup fees for Professional or Team plans. Enterprise customers may have implementation fees depending on customisation requirements.',
  },
]

function CheckIcon() {
  return (
    <svg className="w-[18px] h-[18px] text-accent-teal flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

export default function PricingPage() {
  const [annual, setAnnual] = useState(false)

  return (
    <>
      <SEO
        title="WealthSpace Pricing | Affordable AI Software for UK Advisers"
        description="Transparent pricing from £125/month per adviser. Book a personalised demo to see it in action. AI reports, compliance tools, and client portal included."
        keywords="financial adviser software pricing, IFA software cost UK, practice management pricing, AI adviser tools cost, paraplanning software pricing, affordable IFA software, wealth management platform cost"
        url="/pricing"
      />

      {/* Hero */}
      <section className="pt-[100px] pb-[60px] px-6 text-center relative overflow-hidden">
        <div className="absolute -top-[100px] -right-[100px] w-[400px] h-[400px] bg-[radial-gradient(circle,rgba(20,184,166,0.15)_0%,transparent_70%)] rounded-full" />
        <div className="absolute -bottom-[100px] -left-[100px] w-[300px] h-[300px] bg-[radial-gradient(circle,rgba(16,185,129,0.1)_0%,transparent_70%)] rounded-full" />

        <div className="max-w-[800px] mx-auto relative z-[1]">
          <h1 className="font-serif text-[clamp(36px,5vw,52px)] font-normal leading-[1.1] text-brand-light mb-6">
            Simple, <span className="text-accent-teal italic">transparent</span> pricing
          </h1>
          <p className="text-xl text-brand-muted leading-[1.6] mb-10">
            Book a demo and we'll tailor it to your practice.
          </p>

          {/* Toggle */}
          <div className="flex items-center justify-center gap-4">
            <span className={`text-[14px] transition-colors duration-300 ${!annual ? 'text-brand-light' : 'text-brand-subtle'}`}>
              Monthly
            </span>
            <div
              onClick={() => setAnnual(!annual)}
              className={`w-[44px] h-[24px] rounded-xl relative cursor-pointer transition-colors duration-300 ${
                annual ? 'bg-accent-teal' : 'bg-[#374151]'
              }`}
            >
              <div
                className={`absolute top-[3px] left-[3px] w-[18px] h-[18px] bg-white rounded-full transition-transform duration-300 ${
                  annual ? 'translate-x-5' : ''
                }`}
              />
            </div>
            <span className={`text-[14px] transition-colors duration-300 ${annual ? 'text-brand-light' : 'text-brand-subtle'}`}>
              Annual <span className="text-accent-teal font-medium">(Save 10%)</span>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="px-6 pt-10 pb-20 max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`
                bg-gradient-to-br from-white/[0.03] to-white/[0.01]
                border rounded-2xl py-10 px-8 relative
                transition-all duration-400
                hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)]
                ${plan.popular
                  ? 'border-accent-teal/40 shadow-[0_0_60px_rgba(20,184,166,0.15)]'
                  : 'border-white/[0.06]'
                }
              `}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-accent-teal to-emerald-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full whitespace-nowrap">
                  Most Popular
                </div>
              )}

              <h3 className="text-2xl font-semibold text-brand-light mb-2">{plan.name}</h3>
              <p className="text-sm text-brand-muted mb-6">{plan.description}</p>

              <div className="mb-8">
                {plan.monthlyPrice ? (
                  <>
                    <span className="font-serif text-5xl text-brand-light">
                      £{annual ? plan.annualPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-sm text-brand-subtle">{plan.priceLabel}</span>
                  </>
                ) : (
                  <span className="font-serif text-[28px] text-brand-light">Custom pricing</span>
                )}
              </div>

              <ul className="mb-8 space-y-3">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-brand-muted">
                    <CheckIcon />
                    {feature}
                  </li>
                ))}
              </ul>

              <Button
                href={plan.name === 'Enterprise' ? 'mailto:support@wealthspace.ai' : '/book-demo'}
                variant={plan.popular ? 'primary' : 'secondary'}
                className="w-full justify-center"
              >
                {plan.cta}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-6 bg-black/20">
        <div className="max-w-[800px] mx-auto">
          <h2 className="font-serif text-4xl font-normal text-brand-light text-center mb-12">
            Frequently asked <span className="text-accent-teal italic">questions</span>
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl p-6"
              >
                <h3 className="text-lg font-semibold text-brand-light mb-2">{faq.question}</h3>
                <p className="text-[15px] text-brand-muted leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </>
  )
}
